import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Progress } from './ui/progress';
import { Award, Download, Share2, CheckCircle, Loader2, Shield, Calendar, User, Lock, BookOpen } from 'lucide-react';
import { generateCertificate, getUserCertificate, checkCertificateEligibility } from '../services/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const CertificateDisplay = ({ leadId, userName, userEmail, isOpen, onClose }) => {
  const [certificate, setCertificate] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const certificateRef = useRef(null);

  useEffect(() => {
    const fetchCertificateData = async () => {
      setIsLoading(true);
      try {
        const [cert, elig] = await Promise.all([
          getUserCertificate(leadId),
          checkCertificateEligibility(leadId)
        ]);
        setCertificate(cert);
        setEligibility(elig);
      } catch (error) {
        console.error('Error fetching certificate:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen && leadId) {
      fetchCertificateData();
    }
  }, [isOpen, leadId]);

  const handleGenerateCertificate = async () => {
    setIsGenerating(true);
    try {
      const cert = await generateCertificate(leadId);
      setCertificate(cert);
      toast.success('Certificate generated successfully! 🎉');
    } catch (error) {
      console.error('Error generating certificate:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate certificate. Complete all module quizzes first.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    // Create a simple text-based certificate for download
    const certText = `
═══════════════════════════════════════════════════════════════

                    CERTIFICATE OF COMPLETION
                    
                    WSafeTraining.com
                    Free OSHA Safety Training
                    powered by wsafe.ai - patent pending ai technology

═══════════════════════════════════════════════════════════════

    This is to certify that

                    ${certificate?.lead_name || userName}

    has successfully completed the
    
                OSHA Workplace Safety Training Course
    
    covering essential topics including:
    • Hazard Recognition & Risk Assessment
    • Personal Protective Equipment (PPE)
    • Fire Safety & Emergency Preparedness
    • Electrical Safety & Lockout/Tagout
    • Ergonomics & Manual Material Handling
    • OSHA Compliance & Recordkeeping

═══════════════════════════════════════════════════════════════

    Certificate Number: ${certificate?.certificate_number}
    Issue Date: ${new Date(certificate?.issued_at).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })}
    
    Verify at: wsafetraining.com/verify/${certificate?.certificate_number}

═══════════════════════════════════════════════════════════════
`;

    const blob = new Blob([certText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `OSHA-Certificate-${certificate?.certificate_number}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Certificate downloaded!');
  };

  const handleShare = async () => {
    const shareText = `I just completed the OSHA Workplace Safety Training Course from WSafeTraining.com! Certificate #${certificate?.certificate_number}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'OSHA Safety Training Certificate',
          text: shareText,
          url: window.location.origin
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success('Share text copied to clipboard!');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const progressPercent = eligibility ? (eligibility.completed_modules / eligibility.total_modules) * 100 : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold">
            {certificate ? 'Your Certificate' : 'Certificate Requirements'}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="py-12 text-center">
            <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mx-auto mb-4" />
            <p className="text-slate-600">Loading certificate status...</p>
          </div>
        ) : certificate ? (
          <div className="space-y-6">
            {/* Certificate Preview */}
            <div 
              ref={certificateRef}
              className="bg-gradient-to-br from-slate-50 to-slate-100 border-4 border-double border-emerald-600 rounded-lg p-8 text-center"
            >
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <Shield className="w-8 h-8 text-white" />
                </div>
              </div>
              
              <h3 className="text-sm text-slate-500 uppercase tracking-wider mb-2">Certificate of Completion</h3>
              <h2 className="text-xl font-bold text-emerald-700 mb-1">WSafeTraining.com</h2>
              <p className="text-xs text-slate-400 mb-4">powered by wsafe.ai · patent pending ai technology</p>
              
              <p className="text-slate-600 mb-2">This is to certify that</p>
              <p className="text-2xl font-bold text-slate-900 mb-4">{certificate.lead_name}</p>
              
              <p className="text-slate-600 mb-6">has successfully completed the OSHA Workplace Safety Training Course</p>
              
              <div className="flex items-center justify-center gap-6 text-sm text-slate-500 mb-4">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(certificate.issued_at)}
                </div>
                <div className="flex items-center gap-1">
                  <Award className="w-4 h-4" />
                  {certificate.certificate_number}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-center">
              <Button onClick={handleDownload} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Button variant="outline" onClick={handleShare}>
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-6">
            {/* Eligibility Status */}
            <div className="text-center mb-6">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
                eligibility?.eligible ? 'bg-emerald-100' : 'bg-amber-100'
              }`}>
                {eligibility?.eligible ? (
                  <Award className="w-10 h-10 text-emerald-600" />
                ) : (
                  <Lock className="w-10 h-10 text-amber-600" />
                )}
              </div>
              
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {eligibility?.eligible ? 'Ready to Get Certified!' : 'Complete All Modules to Earn Your Certificate'}
              </h3>
              <p className="text-slate-600 text-sm max-w-md mx-auto">
                {eligibility?.message || 'Complete all 8 module quizzes to unlock your certificate of completion.'}
              </p>
            </div>

            {/* Progress Section */}
            {eligibility && (
              <Card className="p-5 bg-slate-50 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-700">Module Quiz Progress</span>
                  <span className="text-sm font-bold text-emerald-600">
                    {eligibility.completed_modules} / {eligibility.total_modules} completed
                  </span>
                </div>
                <Progress value={progressPercent} className="h-3 mb-4" />
                
                {/* Module Checklist */}
                <div className="grid grid-cols-4 gap-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((moduleId) => {
                    const isCompleted = eligibility.completed_module_ids?.includes(moduleId);
                    return (
                      <Link 
                        key={moduleId} 
                        to={`/module/${moduleId}`}
                        onClick={onClose}
                        className={`flex items-center justify-center gap-1 p-2 rounded-lg text-xs font-medium transition-all ${
                          isCompleted 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-white border border-slate-200 text-slate-500 hover:border-emerald-300 hover:text-emerald-600'
                        }`}
                      >
                        {isCompleted ? (
                          <CheckCircle className="w-3.5 h-3.5" />
                        ) : (
                          <BookOpen className="w-3.5 h-3.5" />
                        )}
                        M{moduleId}
                      </Link>
                    );
                  })}
                </div>
              </Card>
            )}
            
            {/* Action Button */}
            {eligibility?.eligible ? (
              <div className="text-center">
                <Button 
                  onClick={handleGenerateCertificate}
                  disabled={isGenerating}
                  size="lg"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Award className="w-4 h-4 mr-2" />
                      Generate My Certificate
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="text-center">
                <Link to="/module/1" onClick={onClose}>
                  <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    <BookOpen className="w-4 h-4 mr-2" />
                    Continue Learning
                  </Button>
                </Link>
                <p className="text-xs text-slate-500 mt-3">
                  Complete each module&apos;s quiz with a passing score to unlock your certificate
                </p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CertificateDisplay;
